import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { apiErrorMessage } from "@/lib/api";
import { Check, ArrowRight, ArrowLeft, Loader2, Mail, Server } from "lucide-react";

const PROVIDERS = [
  { id: "gmail", label: "Gmail / Google Workspace", host: "imap.gmail.com", port: 993, hint: "Use a Google App Password" },
  { id: "outlook", label: "Outlook / Office 365", host: "outlook.office365.com", port: 993, hint: "IMAP must be enabled in Exchange" },
  { id: "imap", label: "Custom IMAP", host: "", port: 993, hint: "Any IMAP-compatible server" },
];

function StepNumber({ n, active, done }) {
  return (
    <div
      className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all ${
        done
          ? "bg-zinc-950 border-zinc-950 text-white"
          : active
          ? "bg-white border-zinc-950 text-zinc-950"
          : "bg-white border-zinc-300 text-zinc-400"
      }`}
    >
      {done ? <Check className="w-4 h-4" /> : n}
    </div>
  );
}

function Stepper({ step }) {
  const steps = [
    { n: 1, label: "Name", sub: "Job identifier" },
    { n: 2, label: "Source", sub: "Where from" },
    { n: 3, label: "Destination", sub: "Where to" },
    { n: 4, label: "Scope", sub: "What to move" },
    { n: 5, label: "Review", sub: "Confirm & start" },
  ];
  return (
    <div className="p-6 bg-zinc-50 border-r border-zinc-200 space-y-1 min-h-full" data-testid="wizard-stepper">
      <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-6">New Migration</p>
      {steps.map((s, i) => (
        <div key={s.n} className="flex items-start gap-3 py-2">
          <div className="flex flex-col items-center">
            <StepNumber n={s.n} active={step === s.n} done={step > s.n} />
            {i < steps.length - 1 && <div className="w-0.5 h-8 bg-zinc-300 my-1" />}
          </div>
          <div className="pt-1">
            <div className={`font-bold text-sm ${step >= s.n ? "text-zinc-950" : "text-zinc-400"}`}>
              {s.label}
            </div>
            <div className="text-xs font-mono text-zinc-500">{s.sub}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function Endpoint({ role, value, onChange }) {
  const provider = PROVIDERS.find((p) => p.id === value.provider) || PROVIDERS[0];
  const canOAuth = value.provider === "gmail" || value.provider === "outlook";
  const oauthProvider = value.provider === "gmail" ? "google" : "microsoft";
  const [oauthBusy, setOauthBusy] = useState(false);

  const connectOAuth = async () => {
    setOauthBusy(true);
    try {
      const { data } = await api.post(`/oauth/${oauthProvider}/authorize`);
      const popup = window.open(data.authorization_url, `${oauthProvider}-oauth`, "width=560,height=720");
      if (!popup) {
        toast.error("Popup blocked. Please allow popups.");
        setOauthBusy(false);
        return;
      }
      const state = data.state;
      // Poll for session completion
      const poll = setInterval(async () => {
        try {
          const { data: s } = await api.get(`/oauth/session/${state}`);
          if (s.status === "connected") {
            clearInterval(poll);
            onChange({
              ...value,
              email: s.email || value.email,
              access_token: s.access_token,
              refresh_token: s.refresh_token,
              auth_method: "oauth",
              password: "",
            });
            toast.success(`Connected as ${s.email}`);
            setOauthBusy(false);
          } else if (s.status === "error") {
            clearInterval(poll);
            toast.error(s.error || "OAuth failed");
            setOauthBusy(false);
          }
        } catch (e) { /* keep polling */ }
        if (popup.closed) {
          clearInterval(poll);
          setOauthBusy(false);
        }
      }, 1500);
    } catch (e) {
      toast.error(apiErrorMessage(e));
      setOauthBusy(false);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <label className="text-sm font-semibold text-zinc-900 mb-2 block">Provider</label>
        <div className="grid grid-cols-1 gap-2">
          {PROVIDERS.map((p) => (
            <button
              key={p.id}
              type="button"
              data-testid={`${role}-provider-${p.id}`}
              onClick={() =>
                onChange({
                  ...value,
                  provider: p.id,
                  host: p.host || value.host,
                  port: p.port,
                  auth_method: "password",
                  access_token: "",
                  refresh_token: "",
                })
              }
              className={`w-full flex items-start gap-3 px-4 py-3 border rounded-md text-left transition-all ${
                value.provider === p.id
                  ? "border-zinc-950 bg-white"
                  : "border-zinc-200 bg-white hover:border-zinc-400"
              }`}
            >
              <Server className={`w-4 h-4 mt-0.5 ${value.provider === p.id ? "text-[#FF4F00]" : "text-zinc-400"}`} />
              <div>
                <div className="font-semibold text-sm">{p.label}</div>
                <div className="font-mono text-xs text-zinc-500">{p.hint}</div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {canOAuth && (
        <div className="border border-zinc-200 rounded-md p-4 bg-zinc-50">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="font-semibold text-sm">Sign in with {value.provider === "gmail" ? "Google" : "Microsoft"}</div>
              <div className="font-mono text-xs text-zinc-500 mt-0.5">
                {value.auth_method === "oauth" && value.access_token
                  ? `Connected as ${value.email}`
                  : "Recommended · no app password needed"}
              </div>
            </div>
            <button
              type="button"
              data-testid={`${role}-oauth-connect`}
              onClick={connectOAuth}
              disabled={oauthBusy}
              className="inline-flex items-center gap-2 bg-zinc-950 hover:bg-zinc-800 text-white text-sm font-semibold px-4 py-2 rounded-md disabled:opacity-50"
            >
              {oauthBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
              {value.auth_method === "oauth" && value.access_token ? "Reconnect" : "Connect"}
            </button>
          </div>
        </div>
      )}

      {value.provider === "imap" && (
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2">
            <label className="text-sm font-semibold text-zinc-900 mb-1.5 block">IMAP Host</label>
            <input
              data-testid={`${role}-host-input`}
              value={value.host}
              onChange={(e) => onChange({ ...value, host: e.target.value })}
              placeholder="imap.example.com"
              className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 font-mono text-sm outline-none focus:border-zinc-950 focus:ring-1 focus:ring-zinc-950"
            />
          </div>
          <div>
            <label className="text-sm font-semibold text-zinc-900 mb-1.5 block">Port</label>
            <input
              data-testid={`${role}-port-input`}
              type="number"
              value={value.port}
              onChange={(e) => onChange({ ...value, port: parseInt(e.target.value || "993", 10) })}
              className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 font-mono text-sm outline-none focus:border-zinc-950 focus:ring-1 focus:ring-zinc-950"
            />
          </div>
        </div>
      )}

      <div>
        <label className="text-sm font-semibold text-zinc-900 mb-1.5 block">Email / Username</label>
        <input
          data-testid={`${role}-email-input`}
          type="email"
          value={value.email}
          onChange={(e) => onChange({ ...value, email: e.target.value })}
          placeholder="mailbox@domain.com"
          className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 outline-none focus:border-zinc-950 focus:ring-1 focus:ring-zinc-950"
        />
      </div>
      <div>
        <label className="text-sm font-semibold text-zinc-900 mb-1.5 block">
          Password / App Password
        </label>
        <input
          data-testid={`${role}-password-input`}
          type="password"
          value={value.password}
          onChange={(e) => onChange({ ...value, password: e.target.value })}
          placeholder="••••••••••"
          className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 outline-none focus:border-zinc-950 focus:ring-1 focus:ring-zinc-950"
        />
        <p className="text-xs font-mono text-zinc-500 mt-1.5">
          Credentials are only used during the transfer job. Never logged.
        </p>
      </div>
    </div>
  );
}

export default function NewMigration() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const [name, setName] = useState("");
  const [source, setSource] = useState({ provider: "gmail", host: "imap.gmail.com", port: 993, email: "", password: "", use_ssl: true, auth_method: "password", access_token: "", refresh_token: "" });
  const [destination, setDestination] = useState({ provider: "outlook", host: "outlook.office365.com", port: 993, email: "", password: "", use_ssl: true, auth_method: "password", access_token: "", refresh_token: "" });
  const [migrateEmails, setMigrateEmails] = useState(true);
  const [migrateContacts, setMigrateContacts] = useState(false);
  const [migrateCalendar, setMigrateCalendar] = useState(false);

  const testConnection = async (endpoint, role) => {
    setBusy(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/migrations/test-connection", endpoint);
      setTestResult({ role, ...data });
      if (data.ok) toast.success(`${role} verified · ${data.folders.length} folders`);
      else toast.error(data.message);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const next = () => setStep((s) => Math.min(5, s + 1));
  const prev = () => setStep((s) => Math.max(1, s - 1));

  const canNext = () => {
    if (step === 1) return name.trim().length > 0;
    if (step === 2) return source.email && (source.password || source.access_token) && source.host;
    if (step === 3) return destination.email && (destination.password || destination.access_token) && destination.host;
    if (step === 4) return migrateEmails || migrateContacts || migrateCalendar;
    return true;
  };

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/migrations", {
        name,
        source,
        destination,
        migrate_emails: migrateEmails,
        migrate_contacts: migrateContacts,
        migrate_calendar: migrateCalendar,
      });
      await api.post(`/migrations/${data.id}/start`);
      toast.success("Migration started");
      navigate(`/migrations/${data.id}`);
    } catch (e) {
      toast.error(apiErrorMessage(e));
      setBusy(false);
    }
  };

  return (
    <div className="p-8 lg:p-12 max-w-6xl">
      <div className="mb-8">
        <Link to="/dashboard" className="text-sm font-mono text-zinc-500 hover:text-zinc-950" data-testid="wizard-back-to-dash">
          ← Dashboard
        </Link>
        <h1 className="text-4xl font-extrabold tracking-tight mt-3">Create migration</h1>
      </div>

      <div className="bg-white border border-zinc-200 rounded-md overflow-hidden grid grid-cols-1 md:grid-cols-[280px_1fr]">
        <Stepper step={step} />

        <div className="p-8 min-h-[520px] flex flex-col">
          {step === 1 && (
            <div className="max-w-lg" data-testid="wizard-step-1">
              <h2 className="text-2xl font-bold mb-2">Name this job</h2>
              <p className="text-zinc-600 mb-8">Something you'll recognize later in the dashboard.</p>
              <label className="text-sm font-semibold text-zinc-900 mb-1.5 block">Migration name</label>
              <input
                data-testid="wizard-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Alice → New Office 365 mailbox"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 outline-none focus:border-zinc-950 focus:ring-1 focus:ring-zinc-950"
                autoFocus
              />
            </div>
          )}

          {step === 2 && (
            <div className="max-w-xl" data-testid="wizard-step-2">
              <h2 className="text-2xl font-bold mb-2">Source mailbox</h2>
              <p className="text-zinc-600 mb-8">Where messages will be read from.</p>
              <Endpoint role="source" value={source} onChange={setSource} />
              <button
                type="button"
                data-testid="wizard-test-source"
                onClick={() => testConnection(source, "source")}
                disabled={busy}
                className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-zinc-950 border border-zinc-300 hover:border-zinc-950 px-4 py-2 rounded-md"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                Test connection
              </button>
              {testResult && testResult.role === "source" && (
                <div
                  data-testid="wizard-test-source-result"
                  className={`mt-4 text-sm px-4 py-3 rounded-md border ${
                    testResult.ok ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-rose-50 border-rose-200 text-rose-800"
                  }`}
                >
                  {testResult.message} {testResult.ok && `· ${testResult.folders.length} folders discovered`}
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="max-w-xl" data-testid="wizard-step-3">
              <h2 className="text-2xl font-bold mb-2">Destination mailbox</h2>
              <p className="text-zinc-600 mb-8">Where messages will land.</p>
              <Endpoint role="destination" value={destination} onChange={setDestination} />
              <button
                type="button"
                data-testid="wizard-test-destination"
                onClick={() => testConnection(destination, "destination")}
                disabled={busy}
                className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-zinc-950 border border-zinc-300 hover:border-zinc-950 px-4 py-2 rounded-md"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                Test connection
              </button>
              {testResult && testResult.role === "destination" && (
                <div
                  data-testid="wizard-test-destination-result"
                  className={`mt-4 text-sm px-4 py-3 rounded-md border ${
                    testResult.ok ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-rose-50 border-rose-200 text-rose-800"
                  }`}
                >
                  {testResult.message} {testResult.ok && `· ${testResult.folders.length} folders discovered`}
                </div>
              )}
            </div>
          )}

          {step === 4 && (
            <div className="max-w-xl" data-testid="wizard-step-4">
              <h2 className="text-2xl font-bold mb-2">Migration scope</h2>
              <p className="text-zinc-600 mb-8">Pick what to move. Emails include all folders and labels.</p>
              <div className="space-y-3">
                {[
                  { id: "emails", label: "Emails + folders + labels", checked: migrateEmails, set: setMigrateEmails, available: true },
                  { id: "contacts", label: "Contacts", checked: migrateContacts, set: setMigrateContacts, available: false },
                  { id: "calendar", label: "Calendar", checked: migrateCalendar, set: setMigrateCalendar, available: false },
                ].map((opt) => (
                  <label
                    key={opt.id}
                    data-testid={`wizard-scope-${opt.id}`}
                    className={`flex items-center justify-between border rounded-md px-4 py-3 cursor-pointer transition-all ${
                      opt.checked ? "border-zinc-950 bg-white" : "border-zinc-200 bg-white hover:border-zinc-400"
                    } ${!opt.available && "opacity-60 cursor-not-allowed"}`}
                  >
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        disabled={!opt.available}
                        checked={opt.checked}
                        onChange={(e) => opt.set(e.target.checked)}
                        className="w-4 h-4 accent-[#FF4F00]"
                        data-testid={`wizard-scope-${opt.id}-checkbox`}
                      />
                      <span className="font-semibold">{opt.label}</span>
                    </div>
                    {!opt.available && (
                      <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 border border-zinc-300 px-2 py-0.5 rounded-full">
                        Coming soon
                      </span>
                    )}
                  </label>
                ))}
              </div>
            </div>
          )}

          {step === 5 && (
            <div className="max-w-2xl" data-testid="wizard-step-5">
              <h2 className="text-2xl font-bold mb-2">Review & start</h2>
              <p className="text-zinc-600 mb-8">Confirm the details below. The transfer will begin immediately.</p>
              <div className="border border-zinc-200 rounded-md divide-y divide-zinc-200 bg-zinc-50">
                <Row k="Name" v={name} />
                <Row k="Source" v={`${source.email} · ${source.host}:${source.port}`} />
                <Row k="Destination" v={`${destination.email} · ${destination.host}:${destination.port}`} />
                <Row k="Scope" v={[
                  migrateEmails && "Emails",
                  migrateContacts && "Contacts",
                  migrateCalendar && "Calendar",
                ].filter(Boolean).join(", ")} />
              </div>
            </div>
          )}

          <div className="mt-auto pt-8 flex items-center justify-between border-t border-zinc-100">
            <button
              type="button"
              data-testid="wizard-prev"
              onClick={prev}
              disabled={step === 1}
              className="inline-flex items-center gap-2 text-sm font-semibold text-zinc-600 hover:text-zinc-950 disabled:opacity-40 disabled:hover:text-zinc-600"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>

            {step < 5 ? (
              <button
                type="button"
                data-testid="wizard-next"
                onClick={next}
                disabled={!canNext()}
                className="inline-flex items-center gap-2 bg-zinc-950 hover:bg-zinc-800 disabled:opacity-40 text-white font-semibold px-5 py-2.5 rounded-md transition-all active:scale-95"
              >
                Continue <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                type="button"
                data-testid="wizard-submit"
                onClick={submit}
                disabled={busy}
                className="inline-flex items-center gap-2 bg-[#FF4F00] hover:bg-[#CC3F00] disabled:opacity-50 text-white font-semibold px-6 py-2.5 rounded-md transition-all active:scale-95"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                Start migration
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="grid grid-cols-3 gap-4 px-5 py-3.5">
      <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">{k}</div>
      <div className="col-span-2 font-semibold text-sm break-words">{v || "—"}</div>
    </div>
  );
}
